(() => {
  if (customElements.get('edvi-collection-filters')) return;

  class EdviCollectionFilters extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.revision = 0;
      this.dialog = this.querySelector('[data-facet-dialog]');
      this.form = this.querySelector('[data-facet-form]');
      this.form.noValidate = true;
      this.trigger = this.querySelector('[data-facet-open]');
      this.status = this.querySelector('[data-facet-status]');
      this.renderedURL = location.href;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.currency = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || 'USD'
      });
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('input', event => this.onInput(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('submit', event => this.onSubmit(event), options);
      this.addEventListener('toggle', event => {
        if (event.target.matches('[data-facet-group]')) {
          event.target.querySelector('summary').setAttribute('aria-expanded', String(event.target.open));
        }
      }, { ...options, capture: true });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      this.dialog.addEventListener('keydown', event => this.trapFocus(event), options);
      this.dialog.addEventListener('pointerdown', event => { this.backdropPress = event.target === this.dialog; }, options);
      addEventListener('popstate', () => {
        clearTimeout(this.inputTimer);
        this.load(new URL(location.href), { history: false });
      }, options);
      this.syncPrices();
      this.classList.add('is-enhanced');
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.inputTimer);
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.openFrame);
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('edvi-facets-open');
      this.events = null;
    }

    open() {
      clearTimeout(this.closeTimer);
      this.closing = false;
      if (!this.dialog.open) {
        this.returnFocus = document.activeElement;
        this.dialog.showModal();
      }
      document.documentElement.classList.add('edvi-facets-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.dialog.querySelector('.edvi-facets__footer').append(this.status);
      this.dialog.getBoundingClientRect();
      this.openFrame = requestAnimationFrame(() => this.dialog.classList.add('is-visible'));
    }

    close() {
      if (!this.dialog.open || this.closing) return;
      this.closing = true;
      cancelAnimationFrame(this.openFrame);
      this.dialog.classList.remove('is-visible');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.reducedMotion.matches) this.dialog.close();
      else this.closeTimer = setTimeout(() => this.dialog.close(), 250);
    }

    afterClose() {
      clearTimeout(this.closeTimer);
      this.closing = false;
      this.dialog.classList.remove('is-visible');
      document.documentElement.classList.remove('edvi-facets-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.append(this.status);
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      else this.trigger.focus({ preventScroll: true });
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const items = [...this.dialog.querySelectorAll('button, a[href], input, summary, [tabindex="0"]')]
        .filter(el => !el.disabled && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      const first = items[0];
      const last = items.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-facet-open]')) this.open();
      if (event.target.closest('[data-facet-close]')) this.close();
      if (event.target === this.dialog && this.backdropPress) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }
      const link = event.target.closest('[data-facet-link], [data-facet-retry], [data-facet-pagination] a');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin) return;
      event.preventDefault();
      clearTimeout(this.inputTimer);
      this.preservePreview(url);
      const isPagination = !!link.closest('[data-facet-pagination]');
      this.load(url, { focusResults: isPagination, focusTrigger: !this.dialog.open });
    }

    onInput(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.closest('[data-facet-price]')) return;
      this.invalidateRequest();
      clearTimeout(this.inputTimer);
      const price = input.closest('[data-facet-price]');
      if (input.type === 'range') this.syncPrice(price, input);
      else this.inputTimer = setTimeout(() => {
        this.syncPrice(price, input);
        this.apply();
      }, 450);
    }

    onChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !this.form.contains(input)) return;
      clearTimeout(this.inputTimer);
      const price = input.closest('[data-facet-price]');
      if (price) this.syncPrice(price, input);
      if (input.type === 'range') this.inputTimer = setTimeout(() => this.apply(), 250);
      else this.apply();
    }

    async onSubmit(event) {
      if (event.target !== this.form) return;
      event.preventDefault();
      clearTimeout(this.inputTimer);
      const loaded = await this.apply();
      if (loaded) this.close();
    }

    preservePreview(url) {
      const current = new URL(location.href);
      for (const name of ['preview_theme_id', '_fd', 'pb', 'country', 'currency', 'preselect']) {
        if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
      }
      if (this.dataset.view) url.searchParams.set('view', this.dataset.view);
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      return url;
    }

    formURL() {
      const url = new URL(location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.') || key === 'sort_by' || /^page(?:_|$)/.test(key) || key === 'section_id') url.searchParams.delete(key);
      }
      for (const [key, value] of new FormData(this.form)) {
        if (String(value).trim() !== '') url.searchParams.append(key, value);
      }
      return url;
    }

    apply() {
      if (!this.syncPrices()) {
        const invalid = this.form.querySelector('[aria-invalid="true"]');
        const group = invalid?.closest('[data-facet-group]');
        if (group) group.open = true;
        this.status.textContent = invalid?.validationMessage || '';
        this.form.reportValidity();
        return Promise.resolve(false);
      }
      if (!this.form.reportValidity()) return Promise.resolve(false);
      return this.load(this.formURL());
    }

    invalidateRequest() {
      this.request?.abort();
      this.revision += 1;
      this.setBusy(false);
      this.dispatchEvent(new CustomEvent('edvi:collection-cancelled', { bubbles: true }));
    }

    setBusy(busy) {
      this.querySelector('[data-facet-results]').setAttribute('aria-busy', String(busy));
      this.querySelector('[data-facet-view]').setAttribute('aria-busy', String(busy));
      this.classList.toggle('is-loading', busy);
    }

    syncPrices() {
      return [...this.querySelectorAll('[data-facet-price]')].map(price => this.syncPrice(price)).every(Boolean);
    }

    syncPrice(price, changed) {
      const limit = Number(price.dataset.max);
      const min = price.querySelector('[data-price-min]');
      const max = price.querySelector('[data-price-max]');
      const low = price.querySelector('[data-range-min]');
      const high = price.querySelector('[data-range-max]');
      let from = min.value === '' ? 0 : Number(min.value);
      let to = max.value === '' ? limit : Number(max.value);
      const clamp = number => Math.max(0, Math.min(limit, Math.round(Number.isFinite(number) ? number : 0)));
      if (changed === low) {
        from = Math.min(clamp(Number(low.value)), clamp(to));
        min.value = String(from);
      }
      if (changed === high) {
        to = Math.max(clamp(Number(high.value)), clamp(from));
        max.value = String(to);
      }
      const outsideBounds = (input, number) => input.validity.badInput || !Number.isInteger(number) || number < 0 || number > limit;
      const minInvalid = outsideBounds(min, from);
      const maxInvalid = outsideBounds(max, to);
      const reversed = !minInvalid && !maxInvalid && from > to;
      const boundsMessage = price.dataset.boundsError.replace('[min]', this.currency.format(0)).replace('[max]', this.currency.format(limit));
      const message = minInvalid || maxInvalid ? boundsMessage : reversed ? price.dataset.orderError : '';
      min.setCustomValidity(minInvalid ? boundsMessage : reversed ? price.dataset.orderError : '');
      max.setCustomValidity(maxInvalid ? boundsMessage : '');
      min.setAttribute('aria-invalid', String(minInvalid || reversed));
      max.setAttribute('aria-invalid', String(maxInvalid));
      const error = price.querySelector('[data-price-error]');
      error.textContent = message;
      error.hidden = !message;
      const visualMin = clamp(from);
      const visualMax = Math.max(visualMin, clamp(to));
      low.value = String(visualMin);
      high.value = String(visualMax);
      low.setAttribute('aria-valuemax', String(visualMax));
      high.setAttribute('aria-valuemin', String(visualMin));
      low.setAttribute('aria-valuetext', this.currency.format(visualMin));
      high.setAttribute('aria-valuetext', this.currency.format(visualMax));
      low.style.zIndex = visualMin === limit ? '2' : '';
      const track = price.querySelector('.edvi-facets__slider');
      track.style.setProperty('--range-start', `${limit ? visualMin / limit * 100 : 0}%`);
      track.style.setProperty('--range-end', `${limit ? visualMax / limit * 100 : 100}%`);
      return !message;
    }

    async load(url, options = {}) {
      this.preservePreview(url);
      if (url.href === this.renderedURL && !this.failedURL) {
        this.invalidateRequest();
        if (options.focusResults) this.focusResults();
        return true;
      }
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      const revision = ++this.revision;
      this.setBusy(true);
      this.status.textContent = this.dataset.loadingLabel;
      this.querySelector('[data-facet-notice]').hidden = true;
      this.querySelector('[data-facet-drawer-error]').hidden = true;
      this.dispatchEvent(new CustomEvent('edvi:collection-loading', { bubbles: true, detail: { url: url.href } }));
      const requestURL = new URL(url);
      requestURL.searchParams.set('section_id', this.dataset.sectionId);
      const timeout = setTimeout(() => controller.abort('timeout'), 15000);
      try {
        const response = await fetch(requestURL, {
          signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(`Collection section: ${response.status}`);
        const html = await response.text();
        if (revision !== this.revision) return false;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const fresh = documentFragment.querySelector('edvi-collection-filters');
        if (!fresh || fresh.dataset.sectionId !== this.dataset.sectionId || fresh.dataset.facetContext !== this.dataset.facetContext) throw new Error('Missing collection section');
        this.render(fresh, options);
        this.renderedURL = url.href;
        this.failedURL = null;
        if (options.history !== false && location.href !== url.href) history.pushState({ ...history.state, edviFilters: true }, '', url);
        this.status.textContent = this.querySelector('[data-facet-count]').textContent.trim();
        this.dispatchEvent(new CustomEvent('edvi:collection-updated', { bubbles: true, detail: { sectionId: this.dataset.sectionId, url: url.href } }));
        return true;
      } catch (error) {
        if (revision !== this.revision || (controller.signal.aborted && controller.signal.reason !== 'timeout')) return false;
        this.failedURL = url.href;
        this.querySelector('[data-facet-error]').textContent = this.dataset.errorLabel;
        this.querySelector('[data-facet-notice]').hidden = false;
        this.querySelector('[data-facet-retry]').href = url.href;
        const drawerError = this.querySelector('[data-facet-drawer-error]');
        drawerError.textContent = this.dataset.errorLabel;
        drawerError.hidden = false;
        this.status.textContent = this.dataset.errorLabel;
        this.dispatchEvent(new CustomEvent('edvi:collection-error', { bubbles: true }));
        return false;
      } finally {
        clearTimeout(timeout);
        if (revision === this.revision) this.setBusy(false);
      }
    }

    render(fresh, options) {
      const fragments = ['[data-facet-count]', '[data-facet-trigger-label]', '[data-facet-active]', '[data-facet-results]', '[data-facet-fields]', '[data-facet-footer-meta]'];
      if (this.querySelector('[data-facet-quick]')) fragments.push('[data-facet-quick]');
      if (fragments.some(selector => !fresh.querySelector(selector))) throw new Error('Incomplete collection section');
      const openGroups = new Set([...this.querySelectorAll('[data-facet-group][open]')].map(el => el.dataset.facetGroup));
      const body = this.querySelector('[data-facet-body]');
      const scrollTop = body.scrollTop;
      const focused = document.activeElement;
      const focusedID = this.contains(focused) ? focused.id : null;
      const inputName = focused instanceof HTMLInputElement ? focused.name : null;
      const inputValue = focused instanceof HTMLInputElement ? focused.value : null;
      for (const selector of fragments) {
        const target = this.querySelector(selector);
        const source = fresh.querySelector(selector);
        target.replaceChildren(...source.childNodes);
        if (selector === '[data-facet-active]') target.hidden = source.hidden;
      }
      this.form.action = fresh.querySelector('[data-facet-form]').action;
      this.querySelectorAll('[data-facet-group]').forEach(group => {
        group.open = openGroups.has(group.dataset.facetGroup);
        group.querySelector('summary').setAttribute('aria-expanded', String(group.open));
      });
      this.syncPrices();
      body.scrollTop = scrollTop;
      if (options.focusResults && !this.dialog.open) this.focusResults();
      else if (this.dialog.open && focused && !focused.isConnected) {
        let replacement;
        if (inputName && ['checkbox', 'radio'].includes(focused.type)) {
          replacement = [...this.form.elements].find(el => el.name === inputName && el.value === inputValue);
        } else if (focusedID) replacement = this.querySelector(`#${CSS.escape(focusedID)}`);
        if (replacement && !replacement.disabled) replacement.focus({ preventScroll: true });
        else this.dialog.querySelector('[data-facet-close]').focus({ preventScroll: true });
      } else if (options.focusTrigger && !focused?.isConnected) this.trigger.focus({ preventScroll: true });
    }

    focusResults() {
      const results = this.querySelector('[data-facet-results]');
      results.focus({ preventScroll: true });
      results.scrollIntoView({ block: 'start', behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }
  }

  customElements.define('edvi-collection-filters', EdviCollectionFilters);
})();
